/**
 * Общие адаптивные значения для консистентности между страницами
 * Breakpoints MUI: xs < 600px, sm >= 600px, md >= 900px, lg >= 1200px
 */

// Размеры аватаров
export const AVATAR_SIZES = {
  // Карточки аккаунтов, персонажей и т.д.
  card: { xs: 32, sm: 48, md: 56 },
  // Маленькие аватары (списки, комментарии)
  small: { xs: 24, sm: 32, md: 40 },
  // Большие аватары (профиль)
  large: { xs: 48, sm: 64, md: 80 },
};

// Размеры шрифтов
export const FONT_SIZES = {
  // Заголовок карточки (логин, имя)
  cardTitle: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
  // Подзаголовок карточки (тип, описание)
  cardSubtitle: { xs: '0.65rem', sm: '0.875rem' },
  // Буква в аватаре
  avatarLetter: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
  // Заголовок страницы (PageHeader)
  pageTitle: { xs: '1rem', sm: '1.5rem', md: '2.125rem' },
  // Описание страницы
  pageDescription: { xs: '0.7rem', sm: '0.875rem' },
  // Кнопки
  button: { xs: '0.75rem', sm: '0.875rem' },
  // Карточки товаров (склад, магазин, инвентарь)
  itemName: { xs: '0.55rem', sm: '0.7rem' },      // Название товара
  itemPrice: { xs: '0.7rem', sm: '0.8rem' },      // Цена товара (крупнее названия)
  itemMuted: { xs: '0.5rem', sm: '0.65rem' },     // Мелкий текст (count, remaining)
  itemButton: { xs: '0.55rem', sm: '0.75rem' },   // Кнопка покупки
  smallCaption: { xs: '0.65rem', sm: '0.75rem' }, // Мелкие подписи/кнопки
};

// Размеры иконок
export const ICON_SIZES = {
  // Иконки в кнопках
  button: { xs: '1rem', sm: '1.25rem' },
  // Иконка PageHeader
  pageHeader: { xs: 24, sm: 32, md: 40 },
  // Иконки действий в карточках
  cardAction: { xs: '1rem', sm: '1.25rem' },
};

// Отступы (в единицах MUI spacing, 1 = 8px)
export const SPACING = {
  // Padding внутри карточки
  cardPadding: { xs: 1, sm: 2 },
  // Gap между карточками в гриде
  gridGap: { xs: 1, sm: 3 },
  // Margin bottom для заголовков
  headerMargin: { xs: 1.5, sm: 4 },
  // Padding для пагинации
  paginationPadding: { xs: 1, sm: 3 },
};

/**
 * Отступ справа у областей прокрутки (`SimpleBar`), в пикселях.
 *
 * `sm` — место под саму полосу скролла. `xs` — полоса на мобиле скрыта глобально
 * (`.simplebar-track { display: none }` в `index.css`), но ноль ставить нельзя: SimpleBar режет
 * содержимое ровно по своей границе, и при дробной ширине колонок (393px → 143.5px) правый край
 * карточки попадает на половину пикселя и при `dpr = 1` срезается вместе с закруглением.
 * 2px больше любой ошибки округления, поэтому запас закрывает вопрос при любой ширине и любом dpr.
 *
 * ЕДИНЫЙ ИСТОЧНИК: брать отсюда во всех областях прокрутки. Магические числа по месту уже разъезжались —
 * в шести местах было одно значение, в двух другое, и дефект вылезал только на части экранов.
 */
export const SCROLL_RESERVE = { xs: 2, sm: 12 };

// Диалоги
export const DIALOG = {
  // Заголовок диалога
  titleFontSize: { xs: '1rem', sm: '1.5rem' },
  // Описание под заголовком
  descriptionFontSize: { xs: '0.7rem', sm: '0.875rem' },
  // Отступы DialogTitle/Content/Actions
  padding: { xs: 1.5, sm: 3 },
  // Размер шрифта контента (labels, input текст)
  contentFontSize: { xs: '0.8rem', sm: '0.875rem' },
  // Размер шрифта helperText
  helperTextFontSize: { xs: '0.65rem', sm: '0.75rem' },
};

// Табы с анимацией (AnimatedTabs)
export const TAB_SIZES = {
  height: { xs: 32, sm: 36 },
  fontSize: { xs: '0.7rem', sm: '0.8rem' },
  iconSize: { xs: '1.1rem', sm: '1.25rem' },
  px: { xs: 0.75, sm: 1.5 },
  py: { xs: 0.25, sm: 0.5 },
};

// Кнопки (GlassButton) - основные action кнопки на страницах
export const BUTTON_SIZES = {
  height: { xs: 36, sm: 42 },
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
};

// FormControl/Select в формах (не в диалогах)
export const FORM_CONTROL = {
  selectPadding: { xs: 1, sm: 1.5 },
};

// Infinite scroll - динамический размер страницы
export const INFINITE_SCROLL_PAGE_SIZE = {
  xs: 12,  // Мобильные (< 600px) - меньше данных, быстрее загрузка
  sm: 20,  // Планшеты (>= 600px)
  md: 30,  // Средние экраны (>= 900px)
  lg: 50,  // Большие экраны (>= 1200px) - больше видимых карточек
};

// Wiki карточки (WikiItemCard, WikiNpcCard, WikiSkillCard)
export const WIKI_CARD = {
  iconSize: { xs: 48, sm: 56, md: 64 },
  padding: { xs: 1.5, sm: 2 },
  titleFontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.95rem' },
  titleMinHeight: { xs: '2.2rem', sm: '2.6rem' },
  detailsFontSize: { xs: '0.6rem', sm: '0.7rem' },
  chipFontSize: { xs: '0.6rem', sm: '0.65rem' },
  chipHeight: { xs: 18, sm: 20 },
  gap: { xs: 0.25, sm: 0.5 },
  iconMargin: { xs: 1, sm: 2 },
  // Short description (краткое описание на карточке)
  shortDescFontSize: { xs: '0.6rem', sm: '0.7rem' },  // same as detailsFontSize
  shortDescLineClamp: 3,
};

// Wiki карточки категорий (WikiCategoryCard)
export const WIKI_CATEGORY_CARD = {
  imageHeight: { xs: 80, sm: 100, md: 120, lg: 140 },
  titleFontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
  padding: { xs: 1, sm: 1.5, md: 2 },
  borderRadius: { xs: 2, sm: 2, md: 3 },
};

// AUTH_FORM - layout константы для форм авторизации
export const AUTH_FORM = {
  // Контейнер
  containerPadding: { xs: 2, sm: 2.5, md: 3 },
  // Заголовок и подзаголовок
  titleFontSize: { xs: '1.5rem', sm: '1.75rem' },
  subtitleFontSize: { xs: '0.875rem', sm: '0.9rem' },
  headerMarginBottom: { xs: 2.5, sm: 2.5 },
  // Поля ввода
  inputMarginBottom: { xs: 1, sm: 2 },
  inputMarginBottomSmall: { xs: 0.5, sm: 1 },
  // Кнопки
  buttonHeight: { xs: 40, sm: 48 },
  // Общие отступы
  gap: { xs: 1, sm: 1.5 },
  marginBottom: { xs: 1.5, sm: 2 },
  // OAuth кнопки (мобильные иконки)
  oauthIconButton: { xs: 40, sm: 48 },
  oauthIconSize: { xs: 20, sm: 24 },
  oauthIconGap: { xs: 1.5, sm: 2 },
  // Компактные значения для очень маленьких экранов (iPhone 5/SE ≤360px)
  compact: {
    containerPadding: 1.5,
    titleFontSize: '1.25rem',
    headerMarginBottom: 1.5,
    inputMarginBottom: 1.5,
    inputMarginBottomSmall: 1,
    buttonHeight: 36,
    marginBottom: 1.5,
    gap: 0.5,
    helperTextFontSize: '0.6rem',
  },
};

// Публичные страницы контента (Terms, Privacy)
export const PUBLIC_PAGE = {
  // Главный заголовок страницы
  titleFontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
  titleMarginBottom: { xs: 3, sm: 6 },
  // Преамбула/вступительный текст
  preambleFontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
  preambleMarginBottom: { xs: 3, sm: 5 },
  // Секции (блоки с контентом)
  sectionMarginBottom: { xs: 3, sm: 6 },
  sectionPadding: { xs: 2, sm: 3 },
  // Заголовок секции (h5)
  sectionTitleFontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
  sectionTitleMarginBottom: { xs: 2, sm: 3 },
  // Контент секции
  sectionContentFontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
  sectionContentMarginBottom: { xs: 2, sm: 3 },
  // Пункты списка
  listItemFontSize: { xs: '0.8rem', sm: '0.875rem' },
  listMarginLeft: { xs: 1, sm: 3 },
  // Footer текст секции
  footerFontSize: { xs: '0.85rem', sm: '1rem' },
  // Компактные значения для очень маленьких экранов (≤360px)
  compact: {
    titleFontSize: '1.25rem',
    titleMarginBottom: 2,
    preambleFontSize: '0.8rem',
    preambleMarginBottom: 2,
    sectionMarginBottom: 2,
    sectionPadding: 1.5,
    sectionTitleFontSize: '1rem',
    sectionTitleMarginBottom: 1.5,
    sectionContentFontSize: '0.8rem',
    sectionContentMarginBottom: 1.5,
    listItemFontSize: '0.75rem',
    listMarginLeft: 0.5,
    footerFontSize: '0.8rem',
  },
};

// Hero кнопка на главной странице
export const HERO_BUTTON = {
  height: { xs: 44, sm: 54, md: 62 },
  px: { xs: 3, sm: 5, md: 7 },
  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
  letterSpacing: { xs: '1.5px', sm: '2px', md: '3px' },
  borderRadius: { xs: '8px', sm: '10px', md: '12px' },
};

// Stepper circles (ReferralPage, progress indicators)
export const STEPPER_SIZES = {
  circleSize: { xs: 48, sm: 64, md: 80 },
  circleBorder: { xs: 2, sm: 3, md: 4 },
  lineHeight: { xs: 2, sm: 3, md: 4 },
  iconSize: { xs: 24, sm: 32, md: 40 },
  checkIconSize: { xs: 28, sm: 38, md: 48 },
  itemIconSize: { xs: 28, sm: 38, md: 48 },
  percentFontSize: { xs: '0.8rem', sm: '1rem', md: '1.25rem' },
};

// Wiki Detail страницы (ArmorDetail, WeaponDetail, ItemDetail, NpcDetail, SkillDetail)
export const WIKI_DETAIL = {
  // Заголовок предмета (name)
  titleFontSize: FONT_SIZES.cardTitle,
  // Описание предмета
  descriptionFontSize: FONT_SIZES.cardSubtitle,
  // Заголовки секций (Basic Info, Properties, etc.)
  sectionTitleFontSize: { xs: '0.75rem', sm: '0.875rem' },
  // Размер иконки предмета в хедере
  iconSize: { xs: 40, sm: 64 },
  // Чипы свойств
  chipHeight: { xs: 20, sm: 24 },
  chipFontSize: { xs: '0.65rem', sm: '0.8rem' },
  chipPx: { xs: 0.75, sm: 1 },
  // Мелкий текст в карточках (level, chance)
  smallTextFontSize: { xs: '0.65rem', sm: '0.75rem' },
  // Название предмета в списках (ingredients, sources)
  itemNameFontSize: { xs: '0.75rem', sm: '0.8rem' },
  // Мини-чипы (chance, count в Sources/Recipes табах)
  miniChipHeight: { xs: 16, sm: 18 },
  miniChipFontSize: { xs: '0.6rem', sm: '0.65rem' },
  // Boolean свойства чипы (в Properties секции)
  booleanChipFontSize: { xs: '0.6rem', sm: '0.75rem' },
};
